import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import FeaturedGames from '../../components/FeaturedGames/FeaturedGames'
import rawgApi from '../../data/api/rawg/rawg-api'
import { getLoggedInUser } from '../../data/auth/auth-storage'

type FeaturedGame = {
  id?: number | string
  title: string
  desc: string
  img?: string
  platforms?: string[]
  genres?: string[]
  released?: string
  rating?: number
  summary?: string
}

type RawgApiResult = {
  results?: Array<{
    id?: number
    name: string
    released?: string
    rating?: number
    background_image?: string
    platforms?: Array<{ platform?: { name?: string } }>
    genres?: Array<{ name?: string }>
    slug?: string
    description_raw?: string
    esrb_rating?: { name?: string } | null
    age_ratings?: Array<{ rating?: { name?: string; slug?: string } | null }>
  }>
}

const ADULT_CONTENT_TERMS = ['adult only', 'adults only', 'mature', 'explicit', 'nudity', 'sex', 'sexual', 'porn', 'nsfw', 'erotic', 'hentai', 'fetish', 'strip', 'xxx', '18+', 'erotica', 'gore', 'violence', 'blood', 'crime', 'war', 'pregnation']

const isFamilyFriendlyGame = (game: NonNullable<RawgApiResult['results']>[number]) => {
  const ratingName = game.esrb_rating?.name?.toLowerCase() ?? ''
  const ageRatingNames = (game.age_ratings || [])
    .map((entry: { rating?: { name?: string; slug?: string } | null }) => entry.rating?.name?.toLowerCase() ?? entry.rating?.slug?.toLowerCase() ?? '')
    .filter(Boolean)

 const normalizedTerms = [ratingName, ...ageRatingNames]
  const adultMatch = normalizedTerms.some((value) =>
    ['adults only', 'adult only', '18+', 'ao'].some((term) =>
      value.includes(term)
    )
  )

  if (adultMatch) {
    return false
  }

  const searchableText = `${game.name ?? ''} ${game.description_raw ?? ''} ${game.slug ?? ''}`.toLowerCase()
  return !ADULT_CONTENT_TERMS.some((term) => searchableText.includes(term))
}

const HomePage = (): React.JSX.Element => {
  const [featured, setFeatured] = useState<FeaturedGame[]>([])
  const [loadingFeatured, setLoadingFeatured] = useState(true)
  const [featuredError, setFeaturedError] = useState('')

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        setLoadingFeatured(true)
        setFeaturedError('')

        const daySeed = Math.floor(Date.now() / 86400000)
        const pageSize = 24
        const pageCandidates = Array.from({ length: 4 }, (_, index) => ((daySeed + index) % 6) + 1)

        const aggregateResponses = await Promise.all(
          pageCandidates.map((page) => rawgApi.getFeaturedGames(page, pageSize))
        )

        const aggregateResults = aggregateResponses.flatMap((response) => {
          const data: RawgApiResult = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
          return data.results || []
        })

        const loggedInUser = getLoggedInUser()
          const allowAdultContent =
            loggedInUser?.allowAdultContent ?? false

                     
       const familyFriendlyGames = allowAdultContent ? aggregateResults : aggregateResults.filter(isFamilyFriendlyGame)
        const uniqueGames = familyFriendlyGames.filter((game, index, self) => {
          const key = game.id ?? game.slug ?? game.name
          return self.findIndex((candidate) => (candidate.id ?? candidate.slug ?? candidate.name) === key) === index
        })

        const featuredPool = [...uniqueGames]
          .filter((game) => typeof game.rating === 'number' || typeof game.id === 'number')
          .sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0))

        const mapped = featuredPool.map((game) => ({
          id: game.id ?? game.slug,
          title: game.name,
          desc: `Rating: ${(game.rating ?? 0).toFixed(1)}${game.released ? ` | Released: ${new Date(game.released).toLocaleDateString('en-US')}` : ''}`,
          img: game.background_image,
          platforms: (game.platforms || []).map((entry) => entry.platform?.name).filter(Boolean) as string[],
          genres: (game.genres || []).map((genre) => genre.name).filter(Boolean) as string[],
          released: game.released,
          rating: game.rating,
          summary: game.description_raw || 'Explore this title and discover what makes it stand out.'
        }))

        if (mapped.length === 0) {
          setFeaturedError('No games were returned from the API right now.')
          setFeatured([])
          return
        }

        const startIndex = daySeed % Math.max(mapped.length - 5, 1)
        const slice = mapped.slice(startIndex, startIndex + 6)
        const dailySelection = slice.length === 6 ? slice : [...slice, ...mapped.slice(0, 6 - slice.length)]

        setFeatured(dailySelection)
      } catch (error) {
        console.error(error)
        setFeaturedError('Unable to load featured games right now.')
      } finally {
        setLoadingFeatured(false)
      }
    }

    loadFeatured()
  }, [])

  return (
    <Box sx={{ width: '100%' }}>
      <div className="hero">
        <div className="page-header" style={{ justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0, color: '#f8fbff' }}>NextQuest</h1>
            <p className="muted">Discover, track, and share your gaming journey.</p>
          </div>
        </div>

      <section style={{ marginTop: 20 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <h3 style={{ color: '#f8fbff', margin: 0 }}>Featured Games</h3>
          <Typography variant="body2" className="muted">
            New highlights rotate daily so more titles stay visible.
          </Typography>
        </Box>
        {loadingFeatured && <p className="muted">Loading featured games...</p>}
        {!loadingFeatured && featuredError && <p>{featuredError}</p>}
        {!loadingFeatured && !featuredError && <FeaturedGames games={featured} />}
      </section>
      </div>
    </Box>
  )
}

export default HomePage